import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, MapPin, Pencil, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AddressFields,
  cleanAddress,
  emptyAddress,
  formatAddress,
  hasAddress,
  toAddress,
  validateAddress,
} from "@/components/AddressFields";
import { useAuth } from "@/context/AuthContext";
import { useApiError } from "@/lib/useApiError";

const zelfdeAdres = (a, b) =>
  ["straat", "postcode", "plaats", "land"].every(
    (k) => (a?.[k] || "").trim().toLowerCase() === (b?.[k] || "").trim().toLowerCase(),
  );

/**
 * Bezorg- en factuuradres in Mijn account. Een wijziging is een aanvraag:
 * het CRM moet die opnieuw goedkeuren voordat het adres echt verandert.
 */
export const AddressSection = () => {
  const { t } = useTranslation();
  const { account, adresWijziging, refreshAccount } = useAuth();
  const toMessage = useApiError();

  const org = account?.organisatie || {};
  const bezorgBron = org.bezorgadres || org.adres || null;
  const factuurBron = org.factuuradres || null;
  const wachtOpGoedkeuring = Boolean(org.adres_wijziging_open);

  const [open, setOpen] = useState(false);
  const [bezorg, setBezorg] = useState(emptyAddress);
  const [factuur, setFactuur] = useState(emptyAddress);
  const [zelfde, setZelfde] = useState(true);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [verstuurd, setVerstuurd] = useState(false);

  // Formulier vullen met de huidige adressen zodra het opengaat.
  useEffect(() => {
    if (!open) return;
    const b = bezorgBron ? toAddress(bezorgBron) : emptyAddress();
    const f = factuurBron ? toAddress(factuurBron) : b;
    setBezorg(b);
    setFactuur(f);
    setZelfde(!factuurBron || zelfdeAdres(b, f));
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const setAdres = (soort, veld, waarde) => {
    (soort === "bezorg" ? setBezorg : setFactuur)((prev) => ({ ...prev, [veld]: waarde }));
    setErrors((prev) => ({ ...prev, [`${soort}.${veld}`]: undefined }));
  };

  const versturen = async () => {
    const melding = t("auth.register.errors.required");
    const fouten = {
      ...validateAddress("bezorg", bezorg, melding),
      ...(zelfde ? {} : validateAddress("factuur", factuur, melding)),
    };
    setErrors(fouten);
    if (Object.keys(fouten).length > 0) return;

    setBusy(true);
    try {
      const bezorgadres = cleanAddress(bezorg);
      await adresWijziging(bezorgadres, zelfde ? bezorgadres : cleanAddress(factuur));
      setVerstuurd(true);
      setOpen(false);
      toast.success(t("account.address.submitted"));
      try {
        await refreshAccount();
      } catch (e) {
        /* melding is al gegeven */
      }
    } catch (err) {
      // Zolang het CRM-endpoint nog niet live is, krijgen we een 404.
      setErrors({ form: err?.status === 404 ? t("account.address.notAvailable") : toMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const blokken = [
    { key: "bezorg", label: t("auth.register.address.shipping"), bron: bezorgBron },
    { key: "factuur", label: t("auth.register.address.billing"), bron: factuurBron || bezorgBron },
  ];

  return (
    <section data-testid="account-address-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
          <MapPin className="h-4 w-4 text-primary" aria-hidden="true" />
          {t("account.address.title")}
        </h2>
        {!open && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOpen(true)}
            disabled={wachtOpGoedkeuring || verstuurd}
            data-testid="account-address-edit-button"
          >
            <Pencil className="mr-2 h-4 w-4" aria-hidden="true" />
            {t("account.address.edit")}
          </Button>
        )}
      </div>

      {(wachtOpGoedkeuring || verstuurd) && (
        <Alert
          className="mt-4 border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/10"
          data-testid="account-address-pending"
        >
          <Clock className="h-4 w-4 text-[hsl(var(--warning))]" aria-hidden="true" />
          <AlertDescription>{t("account.address.pending")}</AlertDescription>
        </Alert>
      )}

      <dl className="mt-5 grid gap-5 sm:grid-cols-2">
        {blokken.map(({ key, label, bron }) => (
          <div key={key} data-testid={`account-address-${key}`}>
            <dt className="text-xs uppercase tracking-widest text-muted-foreground">{label}</dt>
            <dd className="mt-1 text-sm font-medium">
              {hasAddress(bron) ? (
                formatAddress(bron)
              ) : (
                <span className="font-normal text-muted-foreground" data-testid={`account-address-${key}-empty`}>
                  {t("account.address.unknown")}
                </span>
              )}
            </dd>
          </div>
        ))}
      </dl>

      {open && (
        <>
          <Separator className="my-5" />
          {errors.form && (
            <Alert variant="destructive" className="mb-4" data-testid="account-address-error">
              <AlertDescription>{errors.form}</AlertDescription>
            </Alert>
          )}
          <p className="mb-4 text-xs text-muted-foreground" data-testid="account-address-approval-notice">
            {t("account.address.approvalNotice")}
          </p>
          <div className="space-y-5">
            <AddressFields
              soort="bezorg"
              idPrefix="account"
              titel={t("auth.register.address.shipping")}
              adres={bezorg}
              errors={errors}
              onChange={setAdres}
            />
            <div className="flex items-center gap-3 rounded-md border bg-secondary/60 p-3">
              <Checkbox
                id="account-same-address"
                checked={zelfde}
                onCheckedChange={(v) => setZelfde(Boolean(v))}
                data-testid="account-same-address-checkbox"
              />
              <Label htmlFor="account-same-address" className="cursor-pointer text-sm font-normal">
                {t("auth.register.address.same")}
              </Label>
            </div>
            {!zelfde && (
              <AddressFields
                soort="factuur"
                idPrefix="account"
                titel={t("auth.register.address.billing")}
                adres={factuur}
                errors={errors}
                onChange={setAdres}
              />
            )}
            <div className="flex flex-wrap gap-3">
              <Button onClick={versturen} disabled={busy} data-testid="account-address-submit-button">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
                {busy ? t("account.address.submitting") : t("account.address.submit")}
              </Button>
              <Button variant="ghost" onClick={() => setOpen(false)} data-testid="account-address-cancel-button">
                {t("common.cancel")}
              </Button>
            </div>
          </div>
        </>
      )}
    </section>
  );
};
